import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Check, 
  X,
  Zap, 
  Crown,
  Flame,
  Calculator,
  Eye,
  BarChart3,
  Bell,
  BookOpen,
  Headphones,
  ChevronRight,
  Star,
  Loader2,
  Settings,
  CreditCard,
  ArrowRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Subscribe = () => {
  const { language } = useLanguage();
  const { isAuthenticated, user } = useAuth();
  const [billingCycle, setBillingCycle] = useState('monthly'); // monthly, quarterly, yearly
  const [loadingTier, setLoadingTier] = useState(null); // Track which tier is loading
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Check for cancelled payment
  useEffect(() => {
    if (searchParams.get('cancelled') === 'true') {
      toast.error(language === 'fr' ? 'Paiement annulé' : 'Payment cancelled');
      // Clean URL
      navigate('/subscribe', { replace: true });
    }
  }, [searchParams, language, navigate]);
  
  // Founders status — live count from backend (source of truth)
  const [foundersStatus, setFoundersStatus] = useState({ total: 100, taken: 0, remaining: 100, free_months: 6, active: true });
  const [claimingFounder, setClaimingFounder] = useState(false);

  useEffect(() => {
    const fetchFoundersStatus = async () => {
      try {
        const res = await axios.get(`${API}/stripe/founders/status`);
        setFoundersStatus(res.data);
      } catch (e) {
        // Fallback to defaults (100 remaining)
      }
    };
    fetchFoundersStatus();
  }, []);

  const foundersTaken = foundersStatus.taken;
  const foundersTotal = foundersStatus.total;
  const foundersRemaining = foundersStatus.remaining;

  // Pick up referral code from /r/{code} landing (stored in localStorage as `tradex_affiliate`)
  const [referral, setReferral] = useState(null);
  useEffect(() => {
    try {
      const stored = localStorage.getItem('tradex_affiliate');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.code && parsed?.expires_at && new Date(parsed.expires_at) > new Date()) {
          setReferral(parsed);
        }
      }
    } catch (_) { /* ignore */ }
  }, []);

  // Claim the founder offer (free Expert for 6 months, no Stripe checkout)
  const handleClaimFounder = async () => {
    if (!isAuthenticated) {
      // Stash intent so /register (or Google callback) auto-claims the slot
      try { localStorage.setItem('tradex_founder_intent', '1'); } catch (_e) { /* ignore */ }
      toast.info(language === 'fr'
        ? 'Créez votre compte en 30 sec — votre place Fondateur sera réservée automatiquement.'
        : 'Create your account in 30s — your Founder spot will be reserved automatically.');
      navigate('/register');
      return;
    }
    setClaimingFounder(true);
    try {
      const res = await axios.post(`${API}/stripe/founders/claim`);
      if (res.data.status === 'claimed') {
        toast.success(res.data.message || (language === 'fr' ? 'Bienvenue fondateur !' : 'Welcome founder!'));
        // Refresh local state
        setFoundersStatus(prev => ({ ...prev, taken: prev.taken + 1, remaining: Math.max(0, prev.remaining - 1) }));
        // Redirect to dashboard after 1s
        setTimeout(() => navigate('/dashboard'), 1200);
      } else if (res.data.status === 'already_claimed') {
        toast.info(res.data.message || (language === 'fr' ? 'Vous êtes déjà fondateur' : 'You are already a founder'));
      }
    } catch (error) {
      const detail = error.response?.data?.detail || (language === 'fr' ? 'Erreur lors de la réclamation' : 'Claim failed');
      toast.error(detail);
    } finally {
      setClaimingFounder(false);
    }
  };

  // Auto-claim if the user just logged in with a stashed founder intent (from a prior unauth click)
  useEffect(() => {
    let cancelled = false;
    if (!isAuthenticated) return;
    let founderIntent = false;
    try { founderIntent = localStorage.getItem('tradex_founder_intent') === '1'; } catch (_e) { /* ignore */ }
    if (!founderIntent) return;
    // If the current user is already on a paid tier from a Founder claim, just clear the flag
    const alreadyFounder = user?.subscription_source === 'founder';
    if (alreadyFounder) {
      try { localStorage.removeItem('tradex_founder_intent'); } catch (_e) { /* ignore */ }
      return;
    }
    (async () => {
      try {
        const res = await axios.post(`${API}/stripe/founders/claim`);
        if (cancelled) return;
        if (res.data.status === 'claimed') {
          toast.success(res.data.message || (language === 'fr' ? 'Bienvenue fondateur !' : 'Welcome founder!'));
          setFoundersStatus(prev => ({ ...prev, taken: prev.taken + 1, remaining: Math.max(0, prev.remaining - 1) }));
        }
      } catch (_e) {
        // Silent — user can still click the button manually
      } finally {
        try { localStorage.removeItem('tradex_founder_intent'); } catch (_e) { /* ignore */ }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Handle subscription checkout
  const handleSubscribe = async (tierId) => {
    if (!isAuthenticated) {
      toast.error(language === 'fr' ? 'Connectez-vous pour souscrire' : 'Please login to subscribe');
      navigate('/login');
      return;
    }

    setLoadingTier(tierId);
    try {
      const response = await axios.post(`${API}/stripe/checkout`, {
        tier_id: tierId,
        origin_url: window.location.origin,
        affiliate_code: referral?.code || null,
      });

      if (response.data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(
        language === 'fr' 
          ? 'Erreur lors de la création du paiement' 
          : 'Error creating checkout session'
      );
    } finally {
      setLoadingTier(null);
    }
  };

  const getPrice = (basePrice) => {
    if (billingCycle === 'quarterly') return (basePrice * 3 * 0.9).toFixed(0);
    if (billingCycle === 'yearly') return (basePrice * 12 * 0.8).toFixed(0);
    return basePrice;
  };

  const getPriceLabel = () => {
    if (billingCycle === 'quarterly') return language === 'fr' ? '/3 mois' : '/3 months';
    if (billingCycle === 'yearly') return language === 'fr' ? '/an' : '/year';
    return language === 'fr' ? '/mois' : '/month';
  };

  const getDiscount = () => {
    if (billingCycle === 'quarterly') return '-10%';
    if (billingCycle === 'yearly') return '-20%';
    return null;
  };

  const plans = [
    {
      id: 'free',
      name: 'Free',
      price: 0,
      description: language === 'fr' ? 'Pour découvrir TRADEX AI' : 'To discover TRADEX AI',
      icon: Zap,
      color: 'text-slate-400',
      bgColor: 'bg-slate-500/10',
      borderColor: 'border-slate-500/20',
      features: [
        // 🧠 Analyses IA
        { group: language === 'fr' ? '🧠 Analyses IA' : '🧠 AI Analyses', header: true },
        { text: language === 'fr' ? '2 analyses IA offertes (valables 7 jours)' : '2 free AI analyses (valid 7 days)', included: true },
        { text: language === 'fr' ? '1 analyse DCA offerte' : '1 free DCA analysis', included: true },
        { text: language === 'fr' ? 'Setup complet : Entry / SL / TP1 / TP2 / TP100' : 'Full setup: Entry / SL / TP1 / TP2 / TP100', included: true },
        // ⚡ Automatisation exclusive
        { group: language === 'fr' ? '⚡ Automatisation exclusive' : '⚡ Exclusive automation', header: true },
        { text: language === 'fr' ? 'Scan Auto 24/7 (détection setups par IA)' : 'Auto-Scan 24/7 (AI setup detection)', included: false },
        { text: language === 'fr' ? 'Choix du type de trade (Swing / Intraday)' : 'Trade type choice (Swing / Intraday)', included: false },
        // 🔔 Alertes push
        { group: language === 'fr' ? '🔔 Alertes push' : '🔔 Push alerts', header: true },
        { text: language === 'fr' ? 'Alertes push temps réel (Entry, SL, TP)' : 'Real-time push alerts (Entry, SL, TP)', included: false },
        { text: language === 'fr' ? 'Alertes Zone DCA (OTE)' : 'DCA Zone alerts (OTE)', included: false },
        { text: language === 'fr' ? 'Journal de trading complet' : 'Full trading journal', included: false },
        // 🛠 Outils
        { group: language === 'fr' ? '🛠 Outils' : '🛠 Tools', header: true },
        { text: language === 'fr' ? 'Calculateur (10/mois)' : 'Calculator (10/month)', included: true },
        { text: language === 'fr' ? 'Watchlist 5 actifs en temps réel' : 'Real-time watchlist 5 assets', included: true },
        { text: language === 'fr' ? 'Calendrier économique' : 'Economic calendar', included: true },
        { text: language === 'fr' ? 'Formation ICT (les bases)' : 'ICT training (basics)', included: true },
      ],
      cta: language === 'fr' ? 'Commencer gratuitement' : 'Start for free',
      ctaVariant: 'outline'
    },
    {
      id: 'pro',
      name: 'Pro',
      price: 19,
      description: language === 'fr' ? 'Pour les traders actifs' : 'For active traders',
      icon: Crown,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      borderColor: 'border-primary/30',
      popular: true,
      features: [
        // 🧠 Analyses IA
        { group: language === 'fr' ? '🧠 Analyses IA' : '🧠 AI Analyses', header: true },
        { text: language === 'fr' ? '15 analyses IA de graphiques / mois' : '15 chart AI analyses / month', included: true },
        { text: language === 'fr' ? '3 analyses DCA / mois' : '3 DCA analyses / month', included: true },
        { text: language === 'fr' ? 'Setup ICT complet : OB, FVG, Liquidité' : 'Full ICT setup: OB, FVG, Liquidity', included: true },
        { text: language === 'fr' ? 'Multi-timeframe confluence (15m/1H/4H/1D)' : 'Multi-timeframe confluence (15m/1H/4H/1D)', included: true },
        // ⚡ Automatisation exclusive
        { group: language === 'fr' ? '⚡ Automatisation exclusive' : '⚡ Exclusive automation', header: true },
        { text: language === 'fr' ? 'Scan Auto 24/7 sur 3 actifs de la watchlist' : 'Auto-Scan 24/7 on 3 watchlist assets', included: true },
        { text: language === 'fr' ? 'Choix des actifs modifiable tous les 14 jours' : 'Assets changeable every 14 days', included: true },
        { text: language === 'fr' ? 'Choix du type de trade : Swing OU Intraday' : 'Trade type: Swing OR Intraday', included: true },
        { text: language === 'fr' ? 'Trades pending créés automatiquement' : 'Pending trades created automatically', included: true },
        // 🔔 Alertes push (identiques à Expert)
        { group: language === 'fr' ? '🔔 Alertes push temps réel' : '🔔 Real-time push alerts', header: true },
        { text: language === 'fr' ? 'Entry, TP1/TP2/TP100, SL Warning + hit' : 'Entry, TP1/TP2/TP100, SL Warning + hit', included: true },
        { text: language === 'fr' ? 'Retracement TP1 / TP2' : 'TP1 / TP2 retracement', included: true },
        { text: language === 'fr' ? 'Liquidation Warning + Liquidation (futures)' : 'Liquidation Warning + Liquidation (futures)', included: true },
        { text: language === 'fr' ? 'Ouverture / fermeture marchés US + FR' : 'US + FR market open / close', included: true },
        { text: language === 'fr' ? 'Résultats calendrier économique' : 'Economic calendar results', included: true },
        { text: language === 'fr' ? 'Warning intraday > 24h (clôture ou conversion)' : 'Intraday > 24h warning (close or convert)', included: true },
        { text: language === 'fr' ? 'Alertes Zone DCA OTE' : 'DCA Zone OTE alerts', included: true },
        // 🛠 Outils
        { group: language === 'fr' ? '🛠 Outils' : '🛠 Tools', header: true },
        { text: language === 'fr' ? 'Calculateur illimité' : 'Unlimited calculator', included: true },
        { text: language === 'fr' ? 'Watchlist 20 actifs en temps réel' : 'Real-time watchlist 20 assets', included: true },
        { text: language === 'fr' ? 'Calendrier économique' : 'Economic calendar', included: true },
        { text: language === 'fr' ? 'Formation ICT complète' : 'Full ICT training', included: true },
        { text: language === 'fr' ? 'Journal de trading (réel et paper)' : 'Trading journal (real & paper)', included: true },
        { text: language === 'fr' ? 'Track Record basique' : 'Basic Track Record', included: true },
        { text: language === 'fr' ? 'Analyse détaillée des trades (filtres avancés)' : 'Detailed trade analysis (advanced filters)', included: false },
      ],
      cta: language === 'fr' ? 'Choisir Pro' : 'Choose Pro',
      ctaVariant: 'default'
    },
    {
      id: 'expert',
      name: 'Expert',
      price: 39,
      description: language === 'fr' ? 'Pour les traders sérieux' : 'For serious traders',
      icon: Flame,
      color: 'text-orange-400',
      bgColor: 'bg-orange-500/10',
      borderColor: 'border-orange-500/30',
      features: [
        // 🧠 Analyses IA
        { group: language === 'fr' ? '🧠 Analyses IA illimitées' : '🧠 Unlimited AI Analyses', header: true },
        { text: language === 'fr' ? 'Analyses IA de graphiques illimitées' : 'Unlimited chart AI analyses', included: true },
        { text: language === 'fr' ? 'Analyses DCA illimitées' : 'Unlimited DCA analyses', included: true },
        { text: language === 'fr' ? 'Setup ICT complet : OB, FVG, Liquidité, MSS' : 'Full ICT setup: OB, FVG, Liquidity, MSS', included: true },
        { text: language === 'fr' ? 'Multi-timeframe confluence (15m/1H/4H/1D)' : 'Multi-timeframe confluence (15m/1H/4H/1D)', included: true },
        // ⚡ Automatisation exclusive — LE POINT FORT
        { group: language === 'fr' ? '⚡ Automatisation exclusive' : '⚡ Exclusive automation', header: true },
        { text: language === 'fr' ? '🔥 Scan Auto 24/7 sur TOUTE votre watchlist' : '🔥 Auto-Scan 24/7 on your WHOLE watchlist', included: true },
        { text: language === 'fr' ? 'Changement d\'actifs illimité' : 'Unlimited asset changes', included: true },
        { text: language === 'fr' ? 'Choix du type de trade : Swing OU Intraday' : 'Trade type: Swing OR Intraday', included: true },
        { text: language === 'fr' ? 'Setups détectés automatiquement par l\'IA' : 'AI-detected setups automatically', included: true },
        { text: language === 'fr' ? 'Trades pending créés automatiquement' : 'Pending trades created automatically', included: true },
        { text: language === 'fr' ? 'Alertes Zone DCA OTE (opportunités d\'achat)' : 'DCA Zone OTE alerts (buy opportunities)', included: true },
        // 🔔 Alertes push complètes
        { group: language === 'fr' ? '🔔 Alertes push temps réel' : '🔔 Real-time push alerts', header: true },
        { text: language === 'fr' ? 'Entry, TP1/TP2/TP100, SL Warning + hit' : 'Entry, TP1/TP2/TP100, SL Warning + hit', included: true },
        { text: language === 'fr' ? 'Retracement TP1 / TP2' : 'TP1 / TP2 retracement', included: true },
        { text: language === 'fr' ? 'Liquidation Warning + Liquidation (futures)' : 'Liquidation Warning + Liquidation (futures)', included: true },
        { text: language === 'fr' ? 'Ouverture / fermeture marchés US + FR' : 'US + FR market open / close', included: true },
        { text: language === 'fr' ? 'Résultats calendrier économique' : 'Economic calendar results', included: true },
        { text: language === 'fr' ? 'Warning intraday > 24h (clôture ou conversion)' : 'Intraday > 24h warning (close or convert)', included: true },
        { text: language === 'fr' ? 'Alertes Zone DCA OTE' : 'DCA Zone OTE alerts', included: true },
        // 🛠 Outils illimités
        { group: language === 'fr' ? '🛠 Outils illimités' : '🛠 Unlimited tools', header: true },
        { text: language === 'fr' ? 'Calculateur illimité' : 'Unlimited calculator', included: true },
        { text: language === 'fr' ? 'Watchlist illimitée' : 'Unlimited watchlist', included: true },
        { text: language === 'fr' ? 'Calendrier économique + news temps réel' : 'Economic calendar + real-time news', included: true },
        { text: language === 'fr' ? 'Formation complète + Money Management' : 'Full training + Money Management', included: true },
        { text: language === 'fr' ? 'Journal de trading (réel et paper)' : 'Trading journal (real & paper)', included: true },
        { text: language === 'fr' ? 'Track Record complet (win rates, R:R, historique)' : 'Full Track Record (win rates, R:R, history)', included: true },
        { text: language === 'fr' ? '🔥 Analyse détaillée des trades (tous les filtres)' : '🔥 Detailed trade analysis (all filters)', included: true },
      ],
      cta: language === 'fr' ? 'Choisir Expert' : 'Choose Expert',
      ctaVariant: 'default'
    }
  ];

  return (
    <div className="min-h-[calc(100vh-64px)] p-4 sm:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto space-y-8">
        
        {/* Header */}
        <div className="text-center space-y-4">
          <h1 className="text-4xl sm:text-5xl font-bold">
            {language === 'fr' ? 'Choisissez votre' : 'Choose your'}
            <span className="text-primary"> Plan</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {language === 'fr' 
              ? 'Débloquez le potentiel de l\'IA pour vos décisions de trading' 
              : 'Unlock AI potential for your trading decisions'}
          </p>
        </div>

        {/* Track Record CTA Banner — high visibility */}
        <Link
          to="/track-record"
          data-testid="track-record-cta-top"
          className="group relative block overflow-hidden rounded-2xl border border-primary/30 bg-gradient-to-r from-primary/15 via-emerald-500/10 to-primary/15 hover:border-primary/60 transition-all"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-emerald-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="relative flex flex-col sm:flex-row items-center gap-4 sm:gap-6 p-5 sm:p-6">
            <div className="w-14 h-14 rounded-2xl bg-primary/20 border border-primary/30 flex items-center justify-center flex-shrink-0">
              <BarChart3 className="w-7 h-7 text-primary" />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <div className="flex items-center gap-2 flex-wrap justify-center sm:justify-start mb-1">
                <span className="text-xs font-bold uppercase tracking-widest text-primary">
                  {language === 'fr' ? 'Preuve avant achat' : 'Proof before purchase'}
                </span>
                <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-[10px]">
                  {language === 'fr' ? 'Mis à jour en direct' : 'Live updated'}
                </Badge>
              </div>
              <h2 className="text-lg sm:text-xl font-bold">
                {language === 'fr' ? 'Consultez notre Track Record complet' : 'View our full Track Record'}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {language === 'fr'
                  ? 'Toutes les analyses IA, PnL, profit factor, filtres par marché et profil — 100% transparents.'
                  : 'Every AI analysis, PnL, profit factor, filters by market and profile — 100% transparent.'}
              </p>
            </div>
            <div className="flex items-center gap-2 text-primary font-semibold text-sm sm:text-base whitespace-nowrap">
              {language === 'fr' ? 'Voir les résultats' : 'See results'}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        </Link>

        {/* Referral banner (from /r/{code} landing) */}
        {referral?.code && (
          <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-emerald-500/15 via-emerald-500/10 to-emerald-500/15 border border-emerald-500/30 p-4" data-testid="referral-banner">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <Star className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <div className="text-sm font-bold text-emerald-300">
                    Code parrainage actif — <span className="font-mono">{referral.code}</span>
                    {referral.name ? <span className="text-muted-foreground font-normal"> · via {referral.name}</span> : null}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    -10 % à l&apos;inscription · 50 analyses IA offertes sur Pro mensuel · +3 assets Scan Auto sur Expert mensuel
                  </div>
                </div>
              </div>
              <Button size="sm" variant="ghost" className="text-xs text-muted-foreground hover:text-red-400" onClick={() => { localStorage.removeItem('tradex_affiliate'); setReferral(null); }} data-testid="remove-referral-btn">
                Retirer
              </Button>
            </div>
          </div>
        )}

        {/* Founders Offer Banner */}
        {foundersRemaining > 0 ? (
          <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-orange-500/20 via-red-500/20 to-orange-500/20 border border-orange-500/30 p-6">
            <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/10 rounded-full blur-3xl"></div>
            <div className="relative flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-orange-500/20 flex items-center justify-center">
                  <Flame className="w-7 h-7 text-orange-400" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-xl font-bold text-orange-400">
                      {language === 'fr' ? 'Offre Fondateurs' : 'Founders Offer'}
                    </h3>
                    <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30 animate-pulse">
                      {foundersTaken}/{foundersTotal} {language === 'fr' ? 'places prises' : 'spots taken'}
                    </Badge>
                    {foundersRemaining <= 20 && (
                      <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                        {language === 'fr' ? `Plus que ${foundersRemaining} !` : `Only ${foundersRemaining} left!`}
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {language === 'fr'
                      ? `Les 50 premières places → Expert offert ${foundersStatus.free_months} mois. Les 50 suivantes → Pro offert ${foundersStatus.free_months} mois.`
                      : `First 50 spots → Free Expert ${foundersStatus.free_months} months. Next 50 → Free Pro ${foundersStatus.free_months} months.`}
                    {foundersStatus.next_tier && (
                      <span className="text-orange-400 font-semibold ml-1">
                        {language === 'fr' ? `Prochaine place : ${foundersStatus.next_tier === 'expert' ? 'Expert' : 'Pro'} !` : `Next spot: ${foundersStatus.next_tier === 'expert' ? 'Expert' : 'Pro'}!`}
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-orange-300/70 mt-1">
                    {language === 'fr'
                      ? `⚠️ Après ${foundersStatus.free_months} mois, ajoute une carte pour garder l'accès (sinon retour au plan Free)`
                      : `⚠️ After ${foundersStatus.free_months} months, add a card to keep access (otherwise back to Free plan)`}
                  </p>
                </div>
              </div>
              <Button
                className="bg-orange-500 hover:bg-orange-600 text-white shrink-0"
                onClick={handleClaimFounder}
                disabled={claimingFounder}
                data-testid="claim-founder-btn"
              >
                {claimingFounder ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    {language === 'fr' ? 'Réclamation...' : 'Claiming...'}
                  </>
                ) : (
                  <>
                    {language === 'fr' ? 'Devenir Fondateur' : 'Become a Founder'}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
            {/* Progress bar */}
            <div className="mt-4 h-2 bg-orange-500/10 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-orange-500 to-red-500 rounded-full transition-all duration-500"
                style={{ width: `${(foundersTaken / foundersTotal) * 100}%` }}
              ></div>
            </div>
            <p className="text-xs text-muted-foreground mt-2 text-center">
              {language === 'fr' 
                ? `${foundersRemaining} places restantes sur ${foundersTotal}` 
                : `${foundersRemaining} spots remaining out of ${foundersTotal}`}
            </p>
          </div>
        ) : (
          <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-slate-500/20 via-slate-600/20 to-slate-500/20 border border-slate-500/30 p-6">
            <div className="text-center">
              <h3 className="text-xl font-bold text-slate-400">
                {language === 'fr' ? 'Offre Fondateurs terminée' : 'Founders Offer ended'}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                {language === 'fr' 
                  ? 'Les 100 places ont été prises. Merci à nos fondateurs !' 
                  : 'All 100 spots have been taken. Thanks to our founders!'}
              </p>
            </div>
          </div>
        )}

        {/* Billing Toggle */}
        <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
          <div className="inline-flex items-center gap-1 p-1 rounded-lg bg-secondary/50 border border-white/10">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                billingCycle === 'monthly' 
                  ? 'bg-primary text-primary-foreground' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {language === 'fr' ? 'Mensuel' : 'Monthly'}
            </button>
            <button
              onClick={() => setBillingCycle('quarterly')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                billingCycle === 'quarterly' 
                  ? 'bg-primary text-primary-foreground' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {language === 'fr' ? '3 mois' : '3 months'}
              <Badge variant="secondary" className="text-xs bg-green-500/20 text-green-400">-10%</Badge>
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                billingCycle === 'yearly' 
                  ? 'bg-primary text-primary-foreground' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {language === 'fr' ? 'Annuel' : 'Yearly'}
              <Badge variant="secondary" className="text-xs bg-green-500/20 text-green-400">-20%</Badge>
            </button>
          </div>
          
          {/* Manage Subscription Button - visible for authenticated users */}
          {isAuthenticated && (
            <Link to="/account/settings?tab=subscription" data-testid="manage-subscription-link">
              <Button
                variant="outline"
                className="border-primary/30 hover:bg-primary/10"
                data-testid="manage-subscription-btn"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                {language === 'fr' ? 'Gérer mon abonnement' : 'Manage subscription'}
              </Button>
            </Link>
          )}
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((plan) => {
            const Icon = plan.icon;
            const displayPrice = plan.price === 0 ? 0 : getPrice(plan.price);
            
            return (
              <Card 
                key={plan.id}
                className={`relative ${plan.bgColor} ${plan.borderColor} border-2 ${
                  plan.popular ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : ''
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-primary text-primary-foreground px-3 py-1">
                      <Star className="w-3 h-3 mr-1" />
                      {language === 'fr' ? 'Populaire' : 'Popular'}
                    </Badge>
                  </div>
                )}
                
                <CardHeader className="text-center pb-4">
                  <div className={`w-14 h-14 rounded-full ${plan.bgColor} flex items-center justify-center mx-auto mb-3`}>
                    <Icon className={`w-7 h-7 ${plan.color}`} />
                  </div>
                  <h3 className={`text-2xl font-bold ${plan.color}`}>{plan.name}</h3>
                  <p className="text-sm text-muted-foreground">{plan.description}</p>
                </CardHeader>
                
                <CardContent className="space-y-6">
                  {/* Price */}
                  <div className="text-center">
                    <div className="flex items-baseline justify-center gap-1">
                      <span className={`text-4xl font-bold ${plan.color}`}>
                        {displayPrice}€
                      </span>
                      {plan.price > 0 && (
                        <span className="text-muted-foreground">{getPriceLabel()}</span>
                      )}
                    </div>
                    {plan.price > 0 && getDiscount() && (
                      <p className="text-xs text-green-400 mt-1">
                        {getDiscount()} {language === 'fr' ? 'de réduction' : 'discount'}
                      </p>
                    )}
                  </div>

                  {/* Features grouped by section with equal min-heights for perfect alignment across the 3 plans */}
                  <ul className="space-y-2.5">
                    {(() => {
                      // Parse the flat features array into sections
                      const sections = [];
                      let current = null;
                      plan.features.forEach((f) => {
                        if (f.header) {
                          current = { group: f.group, items: [] };
                          sections.push(current);
                        } else if (current) {
                          current.items.push(f);
                        }
                      });
                      // Fixed min-heights per section (based on max item count across the 3 plans)
                      // Analyses:4 · Automatisation:6 · Alertes:7 · Outils:7 → 44px header + N×32px items
                      const sectionHeights = {
                        'analyses': 44 + 4 * 32,       // ~172px
                        'automatisation': 44 + 6 * 40, // ~284px (some items wrap to 2 lines)
                        'alertes': 44 + 7 * 36,        // ~296px
                        'outils': 44 + 7 * 32,         // ~268px
                      };
                      const getKey = (label) => {
                        const l = (label || '').toLowerCase();
                        if (l.includes('analyses')) return 'analyses';
                        if (l.includes('automatisation') || l.includes('automation')) return 'automatisation';
                        if (l.includes('alertes') || l.includes('alerts')) return 'alertes';
                        if (l.includes('outils') || l.includes('tools')) return 'outils';
                        return 'outils';
                      };
                      return sections.map((section, sIdx) => (
                        <li key={sIdx} style={{ minHeight: sectionHeights[getKey(section.group)] || 200 }}>
                          <div className={`text-[11px] font-bold uppercase tracking-wider ${plan.color} pt-3 pb-2 border-b border-white/5 mb-2`}>
                            {section.group}
                          </div>
                          <ul className="space-y-2">
                            {section.items.map((feature, idx) => (
                              <li key={idx} className="flex items-start gap-2.5">
                                {feature.included ? (
                                  <Check className={`w-4 h-4 ${plan.color} shrink-0 mt-0.5`} />
                                ) : (
                                  <X className="w-4 h-4 text-muted-foreground/40 shrink-0 mt-0.5" />
                                )}
                                <span className={`text-sm leading-snug ${feature.included ? '' : 'text-muted-foreground/50 line-through decoration-muted-foreground/30'}`}>
                                  {feature.text}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </li>
                      ));
                    })()}
                  </ul>

                  {/* CTA */}
                  {plan.id === 'free' ? (
                    <Button 
                      className="w-full"
                      variant={plan.ctaVariant}
                      asChild
                    >
                      <Link to={isAuthenticated ? '/dashboard' : '/login'}>
                        {plan.cta}
                      </Link>
                    </Button>
                  ) : (
                    <Button 
                      className={`w-full ${plan.id === 'expert' ? 'bg-orange-500 hover:bg-orange-600' : ''}`}
                      variant={plan.ctaVariant}
                      onClick={() => handleSubscribe(plan.id)}
                      disabled={loadingTier !== null}
                      data-testid={`subscribe-${plan.id}-btn`}
                    >
                      {loadingTier === plan.id ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {language === 'fr' ? 'Chargement...' : 'Loading...'}
                        </>
                      ) : (
                        plan.cta
                      )}
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Features Comparison */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold text-center mb-8">
            {language === 'fr' ? 'Comparaison détaillée' : 'Detailed comparison'}
          </h2>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-4 px-4 font-medium text-muted-foreground">
                    {language === 'fr' ? 'Fonctionnalité' : 'Feature'}
                  </th>
                  <th className="text-center py-4 px-4 font-medium text-slate-400">Free</th>
                  <th className="text-center py-4 px-4 font-medium text-primary">Pro</th>
                  <th className="text-center py-4 px-4 font-medium text-orange-400">Expert</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-white/5">
                  <td className="py-3 px-4 flex items-center gap-2">
                    <Calculator className="w-4 h-4 text-muted-foreground" />
                    {language === 'fr' ? 'Calculateur' : 'Calculator'}
                  </td>
                  <td className="text-center py-3 px-4">10/mois</td>
                  <td className="text-center py-3 px-4 text-primary">{language === 'fr' ? 'Illimité' : 'Unlimited'}</td>
                  <td className="text-center py-3 px-4 text-orange-400">{language === 'fr' ? 'Illimité' : 'Unlimited'}</td>
                </tr>
                <tr className="border-b border-white/5">
                  <td className="py-3 px-4 flex items-center gap-2">
                    <Eye className="w-4 h-4 text-muted-foreground" />
                    Watchlist
                  </td>
                  <td className="text-center py-3 px-4">5 actifs</td>
                  <td className="text-center py-3 px-4 text-primary">20 actifs</td>
                  <td className="text-center py-3 px-4 text-orange-400">{language === 'fr' ? 'Illimité' : 'Unlimited'}</td>
                </tr>
                <tr className="border-b border-white/5">
                  <td className="py-3 px-4 flex items-center gap-2">
                    <Zap className="w-4 h-4 text-muted-foreground" />
                    {language === 'fr' ? 'Analyses IA' : 'AI Analyses'}
                  </td>
                  <td className="text-center py-3 px-4">2 ({language === 'fr' ? 'offertes' : 'free'})</td>
                  <td className="text-center py-3 px-4 text-primary">15/mois</td>
                  <td className="text-center py-3 px-4 text-orange-400">{language === 'fr' ? 'Illimité' : 'Unlimited'}</td>
                </tr>
                <tr className="border-b border-white/5">
                  <td className="py-3 px-4 flex items-center gap-2">
                    <Zap className="w-4 h-4 text-muted-foreground" />
                    {language === 'fr' ? 'Scan Auto 24/7' : 'Auto-Scan 24/7'}
                  </td>
                  <td className="text-center py-3 px-4"><X className="w-4 h-4 inline text-muted-foreground/50" /></td>
                  <td className="text-center py-3 px-4 text-primary text-xs">{language === 'fr' ? '3 actifs (14j)' : '3 assets (14d)'}</td>
                  <td className="text-center py-3 px-4 text-orange-400 text-xs">{language === 'fr' ? 'Watchlist entière' : 'Whole watchlist'}</td>
                </tr>
                <tr className="border-b border-white/5">
                  <td className="py-3 px-4 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-muted-foreground" />
                    {language === 'fr' ? 'Analyses DCA' : 'DCA Analyses'}
                  </td>
                  <td className="text-center py-3 px-4">1 ({language === 'fr' ? 'offerte' : 'free'})</td>
                  <td className="text-center py-3 px-4 text-primary">3/mois</td>
                  <td className="text-center py-3 px-4 text-orange-400">{language === 'fr' ? 'Illimité' : 'Unlimited'}</td>
                </tr>
                <tr className="border-b border-white/5">
                  <td className="py-3 px-4 flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-muted-foreground" />
                    Journal
                  </td>
                  <td className="text-center py-3 px-4">{language === 'fr' ? 'Limité' : 'Limited'}</td>
                  <td className="text-center py-3 px-4 text-primary"><Check className="w-4 h-4 inline" /></td>
                  <td className="text-center py-3 px-4 text-orange-400"><Check className="w-4 h-4 inline" /></td>
                </tr>
                <tr className="border-b border-white/5">
                  <td className="py-3 px-4 flex items-center gap-2">
                    <Bell className="w-4 h-4 text-muted-foreground" />
                    {language === 'fr' ? 'Alertes TP/SL' : 'TP/SL Alerts'}
                  </td>
                  <td className="text-center py-3 px-4"><X className="w-4 h-4 inline text-muted-foreground/50" /></td>
                  <td className="text-center py-3 px-4 text-primary"><Check className="w-4 h-4 inline" /></td>
                  <td className="text-center py-3 px-4 text-orange-400"><Check className="w-4 h-4 inline" /></td>
                </tr>
                <tr className="border-b border-white/5">
                  <td className="py-3 px-4 flex items-center gap-2">
                    <Zap className="w-4 h-4 text-muted-foreground" />
                    {language === 'fr' ? 'Signaux temps réel' : 'Real-time signals'}
                  </td>
                  <td className="text-center py-3 px-4"><X className="w-4 h-4 inline text-muted-foreground/50" /></td>
                  <td className="text-center py-3 px-4"><X className="w-4 h-4 inline text-muted-foreground/50" /></td>
                  <td className="text-center py-3 px-4 text-orange-400"><Check className="w-4 h-4 inline" /></td>
                </tr>
                <tr className="border-b border-white/5">
                  <td className="py-3 px-4 flex items-center gap-2">
                    <Headphones className="w-4 h-4 text-muted-foreground" />
                    {language === 'fr' ? 'Support prioritaire' : 'Priority support'}
                  </td>
                  <td className="text-center py-3 px-4"><X className="w-4 h-4 inline text-muted-foreground/50" /></td>
                  <td className="text-center py-3 px-4"><X className="w-4 h-4 inline text-muted-foreground/50" /></td>
                  <td className="text-center py-3 px-4 text-orange-400"><Check className="w-4 h-4 inline" /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Track Record CTA */}
        <div className="text-center py-8 border-t border-white/10">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-4">
            <Link to="/track-record" className="text-primary hover:underline flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              {language === 'fr' ? 'Voir notre Track Record' : 'View our Track Record'}
            </Link>
          </div>

          {/* Analysis Packs - available to ALL tiers (permanent credits) */}
          <div className="mt-12 mb-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold mb-2">
                {language === 'fr' ? '⚡ Packs Analyses IA' : '⚡ Analysis Packs'}
              </h2>
              <p className="text-sm text-muted-foreground">
                {language === 'fr'
                  ? 'Crédits permanents (jamais expirés) — Utilisables sur tout compte Free, Pro ou Expert'
                  : 'Permanent credits (never expire) — Available for any Free, Pro or Expert account'}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { id: 'pack_50', credits: 50, price: 25, best: false },
                { id: 'pack_100', credits: 100, price: 40, best: true },
                { id: 'pack_200', credits: 200, price: 70, best: false },
              ].map(pack => (
                <Card key={pack.id} className={`p-5 relative ${pack.best ? 'border-amber-400 border-2' : 'border-white/10'}`}>
                  {pack.best && (
                    <Badge className="absolute -top-3 right-4 bg-amber-500 text-black">
                      {language === 'fr' ? 'Meilleur choix' : 'Best value'}
                    </Badge>
                  )}
                  <div className="text-4xl font-bold text-amber-400">{pack.credits}</div>
                  <div className="text-sm text-muted-foreground mb-3">{language === 'fr' ? 'analyses IA' : 'AI analyses'}</div>
                  <div className="text-3xl font-bold mb-1">{pack.price} €</div>
                  <div className="text-xs text-muted-foreground mb-4">
                    {(pack.price / pack.credits).toFixed(2)} € {language === 'fr' ? '/ analyse' : '/ analysis'}
                  </div>
                  <Button
                    className="w-full bg-amber-500 hover:bg-amber-600 text-black"
                    data-testid={`buy-pack-${pack.id}-btn`}
                    onClick={async () => {
                      if (!isAuthenticated) {
                        toast.error(language === 'fr' ? 'Connectez-vous pour acheter un pack' : 'Login required');
                        navigate('/login');
                        return;
                      }
                      try {
                        const res = await axios.post(`${API}/stripe/packs/checkout?pack_id=${pack.id}`);
                        if (res.data?.url) window.location.href = res.data.url;
                      } catch (e) {
                        toast.error(e.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error'));
                      }
                    }}
                  >
                    {language === 'fr' ? 'Acheter' : 'Buy'}
                  </Button>
                </Card>
              ))}
            </div>
          </div>

          <p className="text-muted-foreground text-sm text-center">
            {language === 'fr' 
              ? 'Des questions ? Contactez-nous à support@tradex-ai.com' 
              : 'Questions? Contact us at support@tradex-ai.com'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Subscribe;
